import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Check, CheckCircle, Loader2, Phone, FileText, Package } from 'lucide-react';
import { fbEvents } from '@/lib/facebook-pixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicQuotePage() {
  const navigate = useNavigate();
  const { settings } = useOutletContext() || {};
  const [machines, setMachines] = useState([]);
  const [clientProfiles, setClientProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  const [form, setForm] = useState({
    machine_id: '',
    machine_name: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    client_company: '',
    client_profile: '',
    wants_financing: false,
    wants_demo: false,
    message: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [machinesRes, profilesRes] = await Promise.all([
        fetch(`${API_URL}/api/public/machines`),
        fetch(`${API_URL}/api/public/client-profiles`)
      ]);
      const machinesData = await machinesRes.json();
      const profilesData = await profilesRes.json();
      setMachines(machinesData);
      setClientProfiles(profilesData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/api/')) return `${API_URL}${url}`;
    return url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.client_email || !form.client_phone) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/public/quote-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          source: 'quick_quote'
        })
      });

      if (!response.ok) throw new Error('Submit failed');

      const result = await response.json();
      
      // Track Facebook Lead event
      fbEvents.submitQuoteRequest({ machine_name: form.machine_name || 'Devis rapide' });

      // Track Google Ads conversion
      if (window.gtag && settings?.google_ads_id && settings?.google_ads_conversion_label) {
        window.gtag('event', 'conversion', {
          send_to: `${settings.google_ads_id}/${settings.google_ads_conversion_label}`,
          event_category: 'Quote',
          event_label: form.machine_name || 'Devis rapide',
        });
      }

      // Track Google Analytics 4 event
      if (window.gtag && settings?.google_analytics_id) {
        window.gtag('event', 'generate_lead', {
          event_category: 'Quote Request',
          event_label: form.machine_name || 'Devis rapide',
        });
      }
      
      setSubmitResult(result);
      setSubmitted(true);
    } catch (error) {
      console.error('Submit error:', error);
      alert('Une erreur est survenue. Veuillez réessayer ou nous contacter directement.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Demande envoyée !</h2>
          <p className="text-slate-600 mb-2">
            Référence : <span className="font-mono font-semibold">{submitResult?.reference}</span>
          </p>
          
          {/* Confirmation email envoyé */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-center gap-2 text-blue-700 font-medium mb-1">
              <FileText className="w-5 h-5" />
              Devis envoyé par email
            </div>
            <p className="text-sm text-blue-600">
              Un devis estimatif a été envoyé à <strong>{form.client_email}</strong>
            </p>
          </div>
          
          <p className="text-slate-600 mb-8">
            Notre équipe vous contactera sous <strong>24 à 48h</strong> pour établir votre devis définitif personnalisé.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/portail')}
              className="w-full py-3 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 transition"
            >
              Retour à l'accueil
            </button>
            <a
              href={`tel:${settings?.contact_phone || '0693526449'}`}
              className="w-full py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Nous appeler
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            Demander un <span className="text-yellow-500">Devis</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl">
            Remplissez ce formulaire pour recevoir un devis personnalisé. Nous vous répondrons rapidement.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-sm p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Machine Selection */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-3">
                      Machine souhaitée (optionnel)
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {machines.slice(0, 6).map(machine => (
                        <button
                          key={machine.id}
                          type="button"
                          onClick={() => setForm({ ...form, machine_id: machine.id, machine_name: machine.name })}
                          className={`text-left p-3 rounded-xl border-2 transition ${
                            form.machine_id === machine.id
                              ? 'border-yellow-500 bg-yellow-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          {getImageUrl(machine.image_url) && (
                            <img
                              src={getImageUrl(machine.image_url)}
                              alt={machine.name}
                              className="w-full h-20 object-cover rounded-lg mb-2"
                            />
                          )}
                          <span className="text-sm font-medium text-slate-800 line-clamp-1">{machine.name}</span>
                        </button>
                      ))}
                    </div>
                    <Link 
                      to="/portail/configurateur"
                      className="inline-flex items-center gap-2 mt-4 text-sm text-yellow-600 hover:text-yellow-700"
                    >
                      Voir toutes les machines et options <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>

                  {/* Contact Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        value={form.client_name}
                        onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="Jean Dupont"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Entreprise
                      </label>
                      <input
                        type="text"
                        value={form.client_company}
                        onChange={(e) => setForm({ ...form, client_company: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="Nom de votre entreprise"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={form.client_email}
                        onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="jean@exemple.com"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Téléphone *
                      </label>
                      <input
                        type="tel"
                        value={form.client_phone}
                        onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="0692 XX XX XX"
                        required
                      />
                    </div>
                  </div>

                  {/* Profile */}
                  {clientProfiles.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Votre profil
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {clientProfiles.map(profile => (
                          <button
                            key={profile.id}
                            type="button"
                            onClick={() => setForm({ ...form, client_profile: profile.code })}
                            className={`p-3 rounded-lg border-2 text-left transition ${
                              form.client_profile === profile.code
                                ? 'border-yellow-500 bg-yellow-50'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <div
                              className="w-3 h-3 rounded-full mb-2"
                              style={{ backgroundColor: profile.color || '#3b82f6' }}
                            />
                            <span className="font-medium text-sm text-slate-800">{profile.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Options */}
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.wants_financing}
                        onChange={(e) => setForm({ ...form, wants_financing: e.target.checked })}
                        className="w-5 h-5 rounded border-slate-300 text-yellow-500 focus:ring-yellow-500"
                      />
                      <span className="text-slate-700">Je souhaite un financement</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.wants_demo}
                        onChange={(e) => setForm({ ...form, wants_demo: e.target.checked })}
                        className="w-5 h-5 rounded border-slate-300 text-yellow-500 focus:ring-yellow-500"
                      />
                      <span className="text-slate-700">🎯 Souhaitez-vous découvrir une de nos machines ?</span>
                    </label>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Décrivez votre projet
                    </label>
                    <textarea
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="Type de travaux, budget estimé, délai souhaité..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 py-4 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 transition disabled:opacity-50"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <FileText className="w-5 h-5" />
                        Demander mon devis
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-24">
                <h3 className="font-semibold text-slate-800 mb-4">Pourquoi demander un devis ?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-slate-600 text-sm">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    Prix personnalisé selon vos besoins
                  </li>
                  <li className="flex items-start gap-2 text-slate-600 text-sm">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    Conseils d'experts pour choisir le bon modèle
                  </li>
                  <li className="flex items-start gap-2 text-slate-600 text-sm">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    Solutions de financement sur mesure
                  </li>
                  <li className="flex items-start gap-2 text-slate-600 text-sm">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    Réponse rapide sous 24h
                  </li>
                </ul>

                <hr className="my-6" />

                <div className="text-center">
                  <p className="text-sm text-slate-500 mb-3">Besoin d'une réponse immédiate ?</p>
                  <a
                    href={`tel:${settings?.contact_phone || '0693526449'}`}
                    className="w-full inline-flex items-center justify-center gap-2 py-3 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition"
                  >
                    <Phone className="w-5 h-5" />
                    {settings?.contact_phone || '0693 52 64 49'}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
